import VError from 'verror';
import _ from 'lodash';
import React, {
  useContext, useEffect, useState,
} from 'react';
import {
  Route, Switch, useParams, Link, Redirect,
} from 'react-router-dom';
import { Button, Modal } from '@janus.team/janus-particles';
import copilot from '@janus.team/janus-copilot/cjs/janus-copilot';
import { useQueryClient } from 'react-query';
import { useQueryAccessGrants } from 'hooks/queries';
import { isRacker } from 'config';
import { proxyFetch } from '../fetch';
import { NewProject } from './new';
import { ProjectsPage } from './project-list/projects-page';

export const filterProjects = (projects) => _.filter(projects, ({ linkedState } = {}) => (linkedState !== 'UNLINKED'));
export const sortProjects = (projects) => _.sortBy(projects, ({
  rackspaceAccountName, rackspaceAccountNumber, name, projectId, serviceLevel,
}) => _.lowerCase(`${rackspaceAccountName}|${rackspaceAccountNumber}|${name}|${projectId}|${serviceLevel}`));

export const CreateProjectButton = () => {
  const rackspaceAccountNumber = useParams()?.rackspaceAccountNumber;
  return (
    <Button className="particles-button--primary" as={Link} to={`/racker/customers/${rackspaceAccountNumber}/projects/new`}>
      <i className="fa fa-plus" />
      {' '}
      Add new project
    </Button>
  );
};

const useFetchPostByRackspaceAccountNumber = (url, fetchConfig, {
  rackspaceAccountNumber, parse, checkResponse, ...proxyFetchOptions
} = {}) => {
  const [state, setState] = useState({ loading: false, error: null, data: null });
  const doFetch = () => {
    setState((previous) => _.merge({}, previous, {
      loading: true, rackspaceAccountNumber, error: null, data: null,
    }));
    proxyFetch(`${url}`, _.merge({}, fetchConfig, { method: 'POST', headers: { 'X-Tenant-Id': rackspaceAccountNumber } }), proxyFetchOptions)
      .then((res) => Promise.all([res.parseJson(), res]))
      .then(([json, res]) => Promise.resolve((checkResponse || _.identity)(json, res)).then(() => (parse || _.identity)(json, res)))
      .then(
        (data) => setState((previous) => _.merge({}, previous, { loading: false, data, error: null })),
        (error) => setState((previous) => _.merge({}, previous, { loading: false, data: null, error })),
      );
  };
  return [state, doFetch];
};
const userIsActive = (activity) => {
  const lastActivity = _.get(activity, 'activity.lastActivity') || 0;
  return ((+new Date()) - lastActivity) <= (5 * 60 * 1000);
};

const startedState = () => ({ loading: true });
const successState = () => ({ loading: false, cached: true, error: null });
const failureState = () => ({ loading: false });

export const useFetchGetByRackspaceAccountNumber = (url, {
  rackspaceAccountNumber, errorMessage, parse, pollingIntervalInSeconds, queryName,
} = {}) => {
  const activity = useContext(copilot.context.activity);
  const [state, setState] = useState({
    loading: true, cached: false, error: null, data: null,
  });
  const [lastPolled, setLastPolled] = useState(0);
  const queryClient = useQueryClient();

  useEffect(() => {
    if (lastPolled && !userIsActive(activity)) {
      return;
    }
    let isMounted = true;
    const timeoutId = setTimeout(() => {
      if (isMounted) setState((previous) => ({ ...previous, ...startedState(), rackspaceAccountNumber }));
      proxyFetch(`${url}`, { headers: { 'X-Tenant-Id': rackspaceAccountNumber } }, { loginOnForbidden: false })
        .then((res) => (res.ok ? res : res.text().then((text) => Promise.reject(new VError(`${errorMessage || 'error'}: ${text}`)))))
        .then(_.method('parseJson')).then(parse || _.identity)
        .then(
          (data) => (isMounted ? setState((previous) => ({ ...previous, ...successState(), data })) : null),
          (error) => (isMounted ? setState((previous) => ({ ...previous, ...failureState(), error })) : null),
        )
        .then(() => (isMounted ? pollingIntervalInSeconds && setLastPolled(+new Date()) : null));
    }, (pollingIntervalInSeconds || 0) * (lastPolled ? 1000 : 0));

    return () => {
      isMounted = false;
      clearTimeout(timeoutId);
    };
  }, [rackspaceAccountNumber, parse, url, pollingIntervalInSeconds, setLastPolled, lastPolled, activity, errorMessage]);
  useEffect(() => {
    if (!queryName) {
      return;
    }
    if (state?.data && rackspaceAccountNumber) {
      queryClient.setQueryData([queryName, rackspaceAccountNumber], state?.data);
    }
  }, [state, queryClient, rackspaceAccountNumber, queryName]);
  return [state, setState];
};

export const GcpConsoleButton = ({
  hasAccess, projectId, email, className,
}) => (hasAccess ? (
  <Button
    as="a"
    onClick={(event) => _.invoke(event, 'stopPropagation')}
    href={`https://console.cloud.google.com/home/dashboard?project=${projectId}&authuser=${email}`}
    target="_blank"
    className={className}
  >
    <i className="fa fa-cloud" style={{ marginRight: '5px' }} />
    Console
  </Button>
) : null);

export const GcpShellButton = ({
  hasAccess, projectId, email, className,
}) => (hasAccess ? (
  <Button
    as="a"
    href={`https://console.cloud.google.com/cloudshell?project=${projectId}&authuser=${email}`}
    target="_blank"
    className={className}
  >
    <i className="fa fa-code" style={{ marginRight: '5px' }} />
    Shell
  </Button>
) : null);

const getPermissionsModalBody = (error) => {
  const errorInfo = VError.info(error);
  const isAutomationServiceAccountUnauthorized = (errorInfo.code === 'AutomationServiceAccountUnauthorized') && errorInfo.missing;

  if (!isAutomationServiceAccountUnauthorized) return null;
  return (
    <div>
      <h3>Missing Permissions</h3>
      <ul>
        { _.map(errorInfo.missing, (permission, index) => (
          <li key={index}>
            {' '}
            {permission}
            {' '}
          </li>
        )) }
      </ul>
    </div>
  );
};

export const GcpRequestAccessButton = ({
  projectId, linkedState, serviceLevel, hasAccess, showPermissionsModal, setShowPermissionsModal, serviceBlocks,
}) => {
  const rackspaceAccountNumber = useParams()?.rackspaceAccountNumber;
  const queryClient = useQueryClient();
  const setAccessGrant = React.useCallback((newAccessGrant) => {
    queryClient.setQueryData(['access-grants', rackspaceAccountNumber], (oldData) => ({
      accessGrants: _.concat(oldData.accessGrants || [], [newAccessGrant]),
    }));
  }, [queryClient, rackspaceAccountNumber]);
  const parseApiError = ({ details, message, code } = {}) => code && (
    new VError({
      info: {
        code,
        ...details,
      },
    }, message)
  );
  const [{ loading, error }, doFetch] = useFetchPostByRackspaceAccountNumber(`${process.env.REACT_APP_GCP_API}/accessGrants`, {
    body: JSON.stringify({ projectId }),
  }, {
    rackspaceAccountNumber,
    loginOnForbidden: false,
    checkResponse: (json, res) => {
      if (res.ok) return res.ok;
      setShowPermissionsModal(true);
      const parseError = parseApiError(_.get(json, 'error', {})) || new VError(`Error requesting access for project ${projectId}`);
      return Promise.reject(parseError);
    },
    parse: (body) => _.tap(_.get(body, 'accessGrant'), setAccessGrant),
  });

  const emptyServiceBlocks = (serviceLevel === 'gcp.service_blocks' && serviceBlocks.length === 0);

  const disabledReason = _.reduce([
    [() => (linkedState !== 'LINKED'), 'Project is not linked'],
    [() => !(/service_blocks|aviator/i).test(serviceLevel), 'Racker Access not enabled for gcp.runway service level. Must be one of [gcp.aviator, gcp.service_blocks].'],
    [() => (emptyServiceBlocks), 'Racker Access not enabled for projects with no services assigned.'],
  ], (result, [test, reason]) => (result || (test() && reason)), '') || undefined;
  const disabled = !!disabledReason;
  const onClick = (event) => {
    _.invoke(event, 'stopPropagation');
    doFetch();
  };

  const closeModal = (event) => {
    _.invoke(event, 'stopPropagation');
    setShowPermissionsModal(false);
  };
  return hasAccess ? null : (
    <>
      {error && !!showPermissionsModal && (
      <Modal size="medium" onClose={closeModal}>
        <Modal.Header>
          <h2>{ projectId }</h2>
        </Modal.Header>
        <Modal.Body>
          <h3>Error</h3>
          <p>
            {' '}
            { error.message }
            {' '}
          </p>
          { getPermissionsModalBody(error) }
        </Modal.Body>
        <Modal.Footer>
          <div><Button type="button" primary onClick={closeModal}>Close</Button></div>
        </Modal.Footer>
      </Modal>
      )}
      <Button
        type="button"
        {...{
          onClick, loading, title: disabledReason, disabled,
        }}
      >
        <i className="fa fa-lock" style={{ marginRight: '5px' }} />
        Request access
      </Button>
    </>
  );
};

const useAccessGrantForProject = (projectId) => {
  const rackspaceAccountNumber = useParams()?.rackspaceAccountNumber;
  const accessGrants = useQueryAccessGrants(rackspaceAccountNumber)?.data?.accessGrants;
  return _.find(accessGrants, (grant) => grant.projectId === projectId && grant.active) || {};
};

export const SmartGCPRequestAccessButton = ({ project, setParentAccessGrant, includeShell }) => {
  const {
    projectId, serviceLevel, linkedState, serviceBlocks,
  } = project || {};

  const accessGrant = useAccessGrantForProject(project?.projectId);
  const [showPermissionsModal, setShowPermissionsModal] = useState(false);
  const hasAccess = !!_.some(accessGrant);
  return (
    <>
      <GcpConsoleButton hasAccess={hasAccess} projectId={projectId} email={_.get(accessGrant, 'gcpEmail')} />
      {includeShell && (<GcpShellButton hasAccess={hasAccess} projectId={projectId} email={_.get(accessGrant, 'gcpEmail')} />)}
      <GcpRequestAccessButton {...{
        projectId, serviceLevel, linkedState, hasAccess, showPermissionsModal, setShowPermissionsModal, serviceBlocks,
      }}
      />
    </>
  );
};

export const PassportButton = ({ project }) => {
  const rackspaceAccountNumber = useParams()?.rackspaceAccountNumber;
  if (!isRacker()) {
    return null;
  }
  const { projectId } = project || {};
  const url = (projectId)
    ? `https://manage.rackspace.com/passport/racker/multi-cloud/${rackspaceAccountNumber}/resources/MANAGED_GCP/${projectId}`
    : `https://manage.rackspace.com/passport/racker/multi-cloud/${rackspaceAccountNumber}/resources`;
  return (
    <>
      <Button as="a" className="particles-button--primary" href={url}>
        Passport
      </Button>
    </>
  );
};

export const Routes = () => (
  <Switch>
    <Route path="/racker/customers/:rackspaceAccountNumber/projects/new" component={NewProject} />
    <Redirect
      from="/racker/customers/:rackspaceAccountNumber/projects/:projectId"
      to="/racker/customers/:rackspaceAccountNumber/projects?project=:projectId"
    />
    <Redirect
      from="/racker/customers/:rackspaceAccountNumber/projects-beta"
      to="/racker/customers/:rackspaceAccountNumber/projects"
    />
    <Route path="/racker/customers/:rackspaceAccountNumber/projects" component={ProjectsPage} />
    <Route path="/racker/customers/:rackspaceAccountNumber" component={ProjectsPage} />
  </Switch>
);
